import React, { useCallback, useState } from 'react'

import type { ColumnDef } from '@tanstack/react-table'
import { format } from 'date-fns'

import GoToMessageIcon from '@/assets/icons/icon-go-to-message.svg'
import SearchIcon from '@/assets/icons/icon-search.svg'
import { CreatorBrowserLink } from '@/components/ui/CreatorBrowserLink.tsx'
import { SafeFormattedText } from '@/components/ui/safe-formatted-message.tsx'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import { DataTable } from '@/components/ui/table/controlled-pagination/DataTable.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import {
  useSenders,
  useSentMessages,
} from '@/modules/log-of-all-messages/query/log-of-all-messages-query.ts'
import { SentMessage } from '@/modules/log-of-all-messages/service/message-history-service.ts'
import { useMessageHistoryFilter } from '@/modules/log-of-all-messages/use-message-history-filter.tsx'
import { TeamAvatar } from '@/modules/team/ui/team-avatar.tsx'
import { usePagination } from '@/utils/pagination/pagination-context.tsx'
import { useDebouncedCallback } from '@/utils/useDebouncedCallback.ts'

const typeOfMessages: Record<string, string> = {
  FollowUpMessage: 'Follow Up Message',
  BulkMessage: 'Mass Message',
  WelcomeMessage: 'Welcome Message',
  NormalMessage: 'Direct Message',
}

const columns: ColumnDef<SentMessage>[] = [
  {
    accessorKey: 'message',
    header: 'Message',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.removed ? (
        <div>
          <Tooltip content="This message was deleted">
            <span className="text-red-500 line-through">
              <SafeFormattedText text={row.original.message} />
            </span>
          </Tooltip>
        </div>
      ) : (
        <SafeFormattedText text={row.original.message} />
      ),
  },
  {
    accessorKey: 'senderName',
    header: 'Sender',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.senderName ? (
        <div className="flex items-center gap-2">
          <TeamAvatar
            name={row.original.senderName}
            avatarSrc={row.original.profilePicture}
            className="h-10 w-10"
          />
          {row.original.senderName}
        </div>
      ) : (
        'CreatorHero'
      ),
  },
  {
    accessorKey: 'fanName',
    header: 'Fan',
    enableSorting: false,
    cell: ({ row }) => (
      <CreatorBrowserLink href={`https://onlyfans.com/u${row.original.fanId}`}>
        <>{row.original.fanName ?? `u${row.original.fanId}`}</>
      </CreatorBrowserLink>
    ),
  },
  {
    accessorKey: 'sentAt',
    header: 'Date',
    cell: ({ row }) =>
      format(new Date(row.original.sentAt), 'LLL dd, y hh:mm a'),
  },
  {
    accessorKey: 'tag',
    header: 'Tag',
    enableSorting: false,
    cell: ({ row }) => typeOfMessages[row.original.tag] || row.original.tag,
  },
  {
    header: 'Actions',
    enableSorting: false,
    cell: ({ row }) => (
      <CreatorBrowserLink
        tooltip="Go to message"
        href={`https://onlyfans.com/my/chats/chat/${row.original.fanId}?firstId=${row.original.onlyfansId}`}
      >
        <GoToMessageIcon className="text-primary-500" />
      </CreatorBrowserLink>
    ),
  },
]

export const MessageHistoryTable = () => {
  const { data, isFetching: messagesFetching } = useSentMessages()

  const { pagination, setPagination, searchQuery, setSearchQuery } =
    usePagination()
  const { filter, setFilter } = useMessageHistoryFilter()

  const [query, setQuery] = useState(searchQuery)
  const setSearchQueryDebounced = useDebouncedCallback(800, setSearchQuery)

  const { data: senders, isFetching: sendersFetching } = useSenders()

  const handleMessageTypeChange = useCallback(
    (value: string) => {
      const newValue = value === 'all' ? undefined : value

      setFilter({ ...filter, messageType: newValue })
    },
    [filter, setFilter],
  )

  const handleSenderChange = useCallback(
    (value: string) => {
      const userId = value === 'all' ? undefined : Number(value)

      setFilter({ ...filter, userId })
    },
    [filter, setFilter],
  )

  return (
    <div>
      <div className="mb-4 flex w-full items-center justify-end gap-4">
        <TextField
          prefix={
            <SearchIcon
              className="flex-shrink-0 text-title"
              height={14}
              width={14}
            />
          }
          placeholder="Search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            setSearchQueryDebounced(event.target.value)
          }}
        />
        <div className="flex items-center gap-4">
          <Select
            onValueChange={handleMessageTypeChange}
            value={filter.messageType ?? 'all'}
          >
            <SelectTrigger className="h-full w-full py-2.5 text-title">
              <SelectValue
                className="truncate"
                placeholder="Type of Message"
              />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup className="text-title">
                <SelectItem value="all">
                  <span className="truncate">All types</span>
                </SelectItem>
                {Object.entries(typeOfMessages).map(([key, value]) => (
                  <SelectItem
                    key={key}
                    value={key}
                  >
                    <span className="truncate">{value}</span>
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          {senders && !sendersFetching ? (
            <Select
              onValueChange={handleSenderChange}
              value={filter?.userId?.toString() ?? 'all'}
            >
              <SelectTrigger className="h-full w-full py-2.5 text-title">
                <SelectValue
                  className="truncate"
                  placeholder="Filter by sender"
                />
              </SelectTrigger>
              <SelectContent className="max-h-[250px]">
                <SelectGroup className="text-title">
                  <SelectItem value="all">
                    <span className="truncate">All senders</span>
                  </SelectItem>
                  {senders?.map((sender) => (
                    <SelectItem
                      key={sender.senderId}
                      value={String(sender.senderId)}
                    >
                      <span className="truncate">{sender.senderName}</span>
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          ) : (
            <div className="h-10 w-64 animate-pulse rounded-md bg-skeleton" />
          )}
        </div>
      </div>
      {data && !messagesFetching && (
        <DataTable
          enablePagination
          enableSorting
          searchColumn="message"
          paginationState={pagination}
          onPaginationChange={setPagination}
          columns={columns}
          data={data?.content ?? []}
          pageCount={data?.totalPages ?? 1}
        />
      )}
      {(!data || messagesFetching) && (
        <div className="mt-2 h-[500px] w-full animate-pulse rounded-md bg-skeleton" />
      )}
    </div>
  )
}
